import React, { useState, useEffect, useContext, useCallback } from "react";
import ActionsContext from "common/ActionsContext";
import BqnContext from "common/BqnContext";
import styled from "styled-components";
import Request from "common/Request";
import ColumnsGrid from "common/layouts/ColumnsGrid";
import NumberInput from "common/NumberInput";
import { TextInputControlled } from "common/TextInputControlled";
import { Logs } from "./Logs";

const doesNothing = () => {};

const LinesInput = ({ defaultValue = 100, onChange = doesNothing }) => {
  const [lines, setLines] = useState(defaultValue);

  const updateLines = () => {
    lines !== null && onChange(lines);
  };
  return (
    <div className="input-group-with-button">
      <NumberInput
        defaultValue={defaultValue}
        onChange={(e, value) => setLines(value)}
        onEnter={updateLines}
        label="Number of lines"
        icon="format_line_spacing"
        hideArrows={true}
      />
      <div className="input-group-add">
        <button
          className="btn btn-link waves-effect margin-t-5"
          onClick={updateLines}
          disabled={lines === null}
        >
          Apply
        </button>
      </div>
    </div>
  );
};

const LoggingContextControlsLayout = styled.div`
  & .input-group-with-button {
    display: flex;
    flex-direction: row;
  }
`;

const LoggingRequest = ({
  lines,
  bqnId,
  big = false,
  name = "log",
  positiveRegex,
  negativeRegex,
}) => {
  const {
    clusterStatus: { mode },
  } = useContext(BqnContext);
  const actions = useContext(ActionsContext);
  const [request, setRequest] = useState(null);

  const doLoad = useCallback(() => {
    const getRetrieval = ({ name, lines, mode }) => {
      const bqnSpec = mode === "standalone" ? "" : ` bqn-id ${bqnId}`;
      const cmd = `show system log ${
        name === "system" ? "messages" : "kernel"
      } lines ${lines}${bqnSpec}`;
      return ifCl.run(cmd);
    };
    const doingRetrieval = getRetrieval({ name, lines, mode });
    setRequest(doingRetrieval);
  }, [lines, bqnId, name, mode]);

  useEffect(() => {
    return actions.recv("do-load", doLoad);
  }, [doLoad]);

  useEffect(() => {
    doLoad();
    return actions.recv(`do-load-bqn-${bqnId}`, doLoad);
  }, [doLoad, bqnId]);
  return request === null ? null : (
    <Request during={request.then((response) => ({ response }))}>
      {(result) => (
        <Logs
          name={name}
          bqnId={bqnId}
          big={big}
          positiveRegex={positiveRegex}
          negativeRegex={negativeRegex}
          text={result.response}
        />
      )}
    </Request>
  );
};

const LoggingContext = ({ name }) => {
  const [lines, setLines] = useState(100);
  const [positiveRegex, setPositiveRegex] = useState("");
  const [negativeRegex, setNegativeRegex] = useState("");

  const {
    bqnId,
    clusterStatus: { mode },
    onlineBqns,
  } = useContext(BqnContext);

  const shouldShowGrid = mode === "cluster" && bqnId === "all";

  return (
    <>
      <LoggingContextControlsLayout>
        <ColumnsGrid columns={4} maxWidth="15rem" rowGap="0">
          <LinesInput
            defaultValue={lines}
            onChange={(value) => setLines(value)}
          />
          <TextInputControlled
            type="text"
            name="positiveRegex"
            label="Positive regex to filter"
            icon="search"
            value={positiveRegex}
            onChange={(e) => setPositiveRegex(e.target.value)}
            addon={
              <button
                className="btn btn-link waves-effect"
                onClick={() => setPositiveRegex("")}
              >
                Remove
              </button>
            }
          />
          <TextInputControlled
            type="text"
            name="negativeRegex"
            label="Negative regex to filter"
            required={true}
            icon="remove_circle_outline"
            value={negativeRegex}
            onChange={(e) => setNegativeRegex(e.target.value)}
            addon={
              <button
                className="btn btn-link waves-effect"
                onClick={() => setNegativeRegex("")}
              >
                Remove
              </button>
            }
          />
        </ColumnsGrid>
      </LoggingContextControlsLayout>

      {shouldShowGrid && (
        <ColumnsGrid
          columns={onlineBqns.length > 1 ? 2 : 1}
          minWidth="7cm"
          gap="15px"
        >
          {onlineBqns.map((bqn) => (
            <div key={bqn.bqnId}>
              <h4> BQN {bqn.bqnId} </h4>
              <LoggingRequest
                name={name}
                bqnId={bqn.bqnId}
                lines={lines}
                positiveRegex={positiveRegex}
                negativeRegex={negativeRegex}
              />
            </div>
          ))}
        </ColumnsGrid>
      )}
      {!shouldShowGrid && (
        <ColumnsGrid columns={1} minWidth="7cm" gap="0px">
          {mode === "cluster" && bqnId !== "all" && <h4> BQN {bqnId} </h4>}
          <LoggingRequest
            name={name}
            bqnId={bqnId}
            big={true}
            lines={lines}
            positiveRegex={positiveRegex}
            negativeRegex={negativeRegex}
          />
        </ColumnsGrid>
      )}
    </>
  );
};

export default LoggingContext;
